import { 
  ImagePlus, 
  History, 
  Settings, 
  HelpCircle, 
  Crown, 
  CreditCard,
  Zap,
  LogOut,
  User,
  ChevronUp,
  Lightbulb,
  Wallet,
  Receipt,
  Lock,
  Users,
  Shuffle,
  Megaphone
} from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { FloopLogo } from "./FloopLogo";
import { CreditsDisplay } from "./CreditsDisplay";
import { PlanBadge } from "./PlanBadge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

const mainNavItems = [
  { title: "Inspiration", url: "/inspiration", icon: Lightbulb, isNew: false },
  { title: "Créer", url: "/create", icon: ImagePlus, isNew: false },
  { title: "Remix", url: "/remix", icon: Shuffle, isNew: false },
  { title: "Ad Studio", url: "/ad-studio", icon: Megaphone, isNew: true },
  { title: "Historique", url: "/history", icon: History, isNew: false },
  { title: "Parrainage", url: "/referral", icon: Users, isNew: false },
];

const accountNavItems = [
  { title: "Mes crédits", url: "/credits", icon: Wallet },
  { title: "Mon abonnement", url: "/subscription", icon: Crown },
  { title: "Paramètres", url: "/settings", icon: Settings },
  { title: "Facturation", url: "/billing", icon: Receipt },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getUserDisplayName, getUserEmail, signOut, isAuthenticated } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la déconnexion",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Déconnexion",
        description: "Vous avez été déconnecté avec succès.",
      });
      navigate("/");
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="p-4">
        <div className="flex items-center justify-between">
          <NavLink to="/">
            <FloopLogo size={collapsed ? "sm" : "md"} showText={!collapsed} />
          </NavLink>
          {!collapsed && <ThemeToggle />}
        </div>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {/* Credits Card with Buy Button */}
        {!collapsed && (
          <div className="px-4 py-3 space-y-3">
            <CreditsDisplay />
            <NavLink to="/payment">
              <button className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-floop text-white font-medium text-sm hover:opacity-90 transition-all shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:scale-[1.02]">
                <Zap className="w-4 h-4" />
                Acheter des crédits
              </button>
            </NavLink>
            <PlanBadge showUpgrade={true} />
          </div>
        )}

        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
            {!collapsed ? "Navigation" : ""}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={item.title}
                  >
                    <NavLink 
                      to={item.url}
                      className={cn(
                        "flex items-center gap-3 rounded-xl transition-all duration-200",
                        isActive(item.url) 
                          ? "bg-gradient-primary text-white shadow-lg shadow-primary/20" 
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.title}</span>
                      {item.isNew && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-accent text-accent-foreground rounded-full animate-pulse">
                          NOUVEAU
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Direct link to Pricing */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive("/pricing")}
                  tooltip="Tarifs"
                >
                  <NavLink 
                    to="/pricing"
                    className={cn(
                      "flex items-center gap-3 rounded-xl transition-all duration-200",
                      isActive("/pricing") 
                        ? "bg-gradient-primary text-white shadow-lg shadow-primary/20" 
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )}
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>Tarifs</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        {/* Account Section - Direct Links */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
            {!collapsed ? "Compte" : ""}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {accountNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={item.title}
                  >
                    <NavLink 
                      to={item.url}
                      className={cn(
                        "flex items-center gap-3 rounded-xl transition-colors",
                        isActive(item.url) 
                          ? "bg-primary/10 text-primary" 
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Help */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive("/help")}
                  tooltip="Aide"
                >
                  <NavLink 
                    to="/help"
                    className={cn(
                      "flex items-center gap-3 rounded-xl transition-colors",
                      isActive("/help") 
                        ? "bg-primary/10 text-primary" 
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )}
                  >
                    <HelpCircle className="w-5 h-5" />
                    <span>Aide</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton 
                  className={cn(
                    "w-full rounded-xl bg-secondary/50 hover:bg-secondary transition-colors",
                    collapsed ? "justify-center" : ""
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-floop flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  {!collapsed && (
                    <>
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground truncate">
                          {isAuthenticated ? getUserDisplayName() : "Invité"}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">
                          {isAuthenticated ? getUserEmail() : "Non connecté"}
                        </span>
                      </div>
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    </>
                  )}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                side="top" 
                align="start"
                className="w-56 bg-card border-border rounded-xl"
              >
                {isAuthenticated ? (
                  <>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                      <NavLink to="/settings">
                        <User className="w-4 h-4 mr-2" />
                        Mon profil
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                      <NavLink to="/settings?tab=password">
                        <Lock className="w-4 h-4 mr-2" />
                        Mot de passe
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                      <NavLink to="/billing">
                        <Receipt className="w-4 h-4 mr-2" />
                        Facturation
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="cursor-pointer rounded-lg text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Se déconnecter
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                    <NavLink to="/auth">
                      <User className="w-4 h-4 mr-2" />
                      Se connecter
                    </NavLink>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
